import React, { useMemo } from 'react'
import { Button, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { CustomField } from '../../types'
import { FormField, TextInput } from '../common/EditDialog'

export type CounterConfigValue = {
  template: string
  padding: number
  start_value: number
  variables: Array<{ name: string; custom_field_id: number | null }>
}

export const RESERVED_COUNTER_VARIABLES = new Set(['year', 'month', 'day', 'date', 'counter'])
export const COUNTER_BUILTIN_VARIABLES = ['year', 'month', 'day', 'date', 'counter'] as const
export const COUNTER_VARIABLE_EXCLUDED_TYPES = new Set(['counter', 'separator', 'documentlink'])
const TEMPLATE_VARIABLE_RE = /\{\{\s*(\w+)\s*\}\}/g

export const parseTemplateVariableNames = (template: string): string[] => {
  const names: string[] = []
  for (const match of template.matchAll(TEMPLATE_VARIABLE_RE)) {
    names.push(match[1])
  }
  return names
}

export const parseTemplateVariables = (template: string): string[] => {
  return Array.from(new Set(parseTemplateVariableNames(template)))
}

export const attributeToVariableName = (name: string, reservedNames: Set<string>): string => {
  let base =
    name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '') || 'attr'
  let candidate = base
  let suffix = 2
  while (reservedNames.has(candidate)) {
    candidate = `${base}_${suffix}`
    suffix += 1
  }
  return candidate
}

export const resolveFieldIdForVariable = (varName: string, fields: CustomField[], existingByName: Map<string, number | null>): number | null => {
  const existing = existingByName.get(varName)
  if (existing != null) {
    return existing
  }
  const match = fields.find((field) => attributeToVariableName(field.name, RESERVED_COUNTER_VARIABLES) === varName)
  return match?.id ?? null
}

export const defaultCounterConfig = (): CounterConfigValue => ({
  template: '{{year}}/{{counter}}',
  padding: 0,
  start_value: 1,
  variables: [],
})

export const normalizeCounterConfig = (cfg?: Partial<CounterConfigValue> | null): CounterConfigValue => ({
  template: cfg?.template || defaultCounterConfig().template,
  padding: cfg?.padding ?? 0,
  start_value: cfg?.start_value ?? 1,
  variables: (cfg?.variables || []).map((item) => ({
    name: item.name,
    custom_field_id: item.custom_field_id ?? null,
  })),
})

export const validateCounterConfig = (cfg: CounterConfigValue, t: (key: string) => string): string[] => {
  const errors: string[] = []
  const template = cfg.template?.trim()
  if (!template) {
    errors.push(`counter_template:${t('counter_template_required')}`)
  } else {
    const counterCount = parseTemplateVariableNames(template).filter((name) => name === 'counter').length
    if (counterCount === 0) {
      errors.push(`counter_template:${t('counter_template_missing_counter')}`)
    } else if (counterCount > 1) {
      errors.push(`counter_template:${t('counter_template_duplicate_counter')}`)
    }
    const unmapped = parseTemplateVariables(template)
      .filter((name) => !RESERVED_COUNTER_VARIABLES.has(name))
      .filter((name) => {
        const mapping = cfg.variables.find((item) => item.name === name)
        return !mapping || mapping.custom_field_id === null
      })
    if (unmapped.length > 0) {
      errors.push(`counter_template:${t('counter_unmapped_variables')}`)
    }
  }
  return errors
}

export const toCounterConfigPayload = (cfg: CounterConfigValue) => ({
  template: cfg.template,
  padding: cfg.padding,
  start_value: cfg.start_value,
  variables: cfg.variables
    .filter((item) => item.custom_field_id !== null)
    .map((item) => ({
      name: item.name,
      custom_field_id: item.custom_field_id,
    })),
})

const appendTokenToTemplate = (template: string, token: string): string => {
  if (template.includes(token)) {
    return template
  }
  return template ? `${template.replace(/\s+$/, '')}_${token}` : token
}

interface CounterConfigEditorProps {
  value: CounterConfigValue
  onChange: (value: CounterConfigValue) => void
  eligibleAttributeFields: CustomField[]
  excludeFieldId?: number | null
  disabled?: boolean
  error?: string
  helpText?: string
}

export const CounterConfigEditor: React.FC<CounterConfigEditorProps> = ({
  value,
  onChange,
  eligibleAttributeFields,
  excludeFieldId = null,
  disabled = false,
  error,
  helpText,
}) => {
  const { t } = useTranslation()

  const availableFields = useMemo(
    () => eligibleAttributeFields.filter((field) => field.id !== excludeFieldId),
    [eligibleAttributeFields, excludeFieldId]
  )

  const syncVariables = (template: string, previous: CounterConfigValue): CounterConfigValue => {
    const customVars = parseTemplateVariables(template).filter((name) => !RESERVED_COUNTER_VARIABLES.has(name))
    const existingByName = new Map(previous.variables.map((item) => [item.name, item.custom_field_id]))
    return {
      ...previous,
      template,
      variables: customVars.map((name) => ({
        name,
        custom_field_id: resolveFieldIdForVariable(name, availableFields, existingByName),
      })),
    }
  }

  const insertBuiltin = (varName: (typeof COUNTER_BUILTIN_VARIABLES)[number]) => {
    if (varName === 'counter') {
      const counterCount = parseTemplateVariableNames(value.template).filter((name) => name === 'counter').length
      if (counterCount >= 1) {
        return
      }
    }
    const token = `{{${varName}}}`
    onChange(syncVariables(appendTokenToTemplate(value.template, token), value))
  }

  const insertAttribute = (field: CustomField) => {
    if (excludeFieldId === field.id) {
      return
    }
    const existingMapping = value.variables.find((item) => item.custom_field_id === field.id)
    const reservedNames = new Set([
      ...parseTemplateVariables(value.template).filter((name) => !RESERVED_COUNTER_VARIABLES.has(name)),
      ...value.variables.map((item) => item.name),
    ])
    const varName = existingMapping?.name ?? attributeToVariableName(field.name, reservedNames)
    const token = `{{${varName}}}`
    const template = appendTokenToTemplate(value.template, token)
    const customVars = parseTemplateVariables(template).filter((name) => !RESERVED_COUNTER_VARIABLES.has(name))
    const existingByName = new Map(value.variables.map((item) => [item.name, item.custom_field_id]))
    onChange({
      ...value,
      template,
      variables: customVars.map((name) => ({
        name,
        custom_field_id:
          name === varName ? field.id : (existingByName.get(name) ?? value.variables.find((item) => item.name === name)?.custom_field_id ?? null),
      })),
    })
  }

  const preview = useMemo(() => {
    const padding = Math.max(0, value.padding || 0)
    const sampleCounter = String(1).padStart(padding, '0')
    const mockValues: Record<string, string> = {
      year: String(new Date().getFullYear()),
      month: String(new Date().getMonth() + 1).padStart(2, '0'),
      day: String(new Date().getDate()).padStart(2, '0'),
      date: new Date().toISOString().slice(0, 10),
      counter: sampleCounter,
    }
    value.variables.forEach((item) => {
      const field = availableFields.find((f) => f.id === item.custom_field_id)
      mockValues[item.name] = field?.name || item.name.toUpperCase()
    })
    return (value.template || '').replace(TEMPLATE_VARIABLE_RE, (_, name: string) => mockValues[name] ?? `{{${name}}}`)
  }, [value, availableFields])

  return (
    <>
      {helpText && <div className="form-text text-muted mb-3">{helpText}</div>}
      <FormField label={t('counter_builtin_attributes')}>
        <div className="d-flex flex-wrap gap-2">
          {COUNTER_BUILTIN_VARIABLES.map((varName) => {
            const counterAlreadyUsed =
              varName === 'counter' && parseTemplateVariableNames(value.template).filter((name) => name === 'counter').length >= 1
            return (
              <Button
                key={varName}
                variant="outline-primary"
                size="sm"
                type="button"
                onClick={() => insertBuiltin(varName)}
                disabled={disabled || counterAlreadyUsed}
                title={t(`counter_builtin_${varName}`)}
              >
                {t(`counter_builtin_${varName}`)}
              </Button>
            )
          })}
        </div>
        <div className="form-text text-muted mt-1">{t('counter_builtin_attributes_help')}</div>
      </FormField>
      {availableFields.length > 0 && (
        <FormField label={t('counter_available_attributes')}>
          <div className="d-flex flex-wrap gap-2">
            {availableFields.map((field) => (
              <Button
                key={field.id}
                variant="outline-secondary"
                size="sm"
                type="button"
                onClick={() => insertAttribute(field)}
                disabled={disabled}
                title={t('counter_insert_attribute_help')}
              >
                {field.name}
              </Button>
            ))}
          </div>
          <div className="form-text text-muted mt-1">{t('counter_insert_attribute_help')}</div>
        </FormField>
      )}
      <FormField label={t('counter_template')} required error={error}>
        <TextInput
          value={value.template}
          onChange={(next) => onChange(syncVariables(next, value))}
          placeholder="{{year}}/{{counter}}"
          autoComplete="off"
          disabled={disabled}
          error={error}
        />
        <div className="form-text text-muted mt-1">{t('counter_template_help')}</div>
      </FormField>
      <FormField label={t('counter_padding')}>
        <Form.Control
          type="number"
          min={0}
          value={value.padding}
          onChange={(e) => onChange({ ...value, padding: parseInt(e.target.value, 10) || 0 })}
          disabled={disabled}
        />
      </FormField>
      <FormField label={t('counter_start_value')}>
        <Form.Control
          type="number"
          min={1}
          value={value.start_value}
          onChange={(e) => onChange({ ...value, start_value: parseInt(e.target.value, 10) || 1 })}
          disabled={disabled}
        />
      </FormField>
      {preview && (
        <div className="form-text mb-3">
          {t('counter_preview')}: <strong>{preview}</strong>
        </div>
      )}
    </>
  )
}
